import { createContext, useContext, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { getRef } from "@/components/ambassadors/RefCapture";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const Ctx = createContext({ enabled: false });

export const PayPalProvider = ({ children }) => {
  const [config, setConfig] = useState({ enabled: false });
  useEffect(() => {
    axios.get(`${API}/payments/paypal/config`).then((r) => setConfig(r.data)).catch(() => setConfig({ enabled: false }));
  }, []);
  if (!config.enabled) return <Ctx.Provider value={config}>{children}</Ctx.Provider>;
  return (
    <Ctx.Provider value={config}>
      <PayPalScriptProvider deferLoading={false} options={{ clientId: config.client_id, currency: config.currency, intent: "capture", components: "buttons", disableFunding: "paylater,card" }}>
        {children}
      </PayPalScriptProvider>
    </Ctx.Provider>
  );
};

export const usePayPal = () => useContext(Ctx);

/**
 * Renders the PayPal button when PayPal is configured.
 * `build()` returns the cart payload ({items, shipping_rate_id, destination, coupon_code}) or null to abort.
 */
export const PayPalButton = ({ build, disabled, testId = "paypal-button" }) => {
  const { enabled } = usePayPal();
  const navigate = useNavigate();
  const buildRef = useRef(build);
  buildRef.current = build; // always price the bag as it is at click time (coupon / donation / delivery may change after mount)
  if (!enabled) return null;

  const createOrder = async () => {
    const payload = buildRef.current();
    if (!payload) throw new Error("incomplete");
    try {
      const r = await axios.post(`${API}/payments/paypal/order`, { ref_code: getRef(), ...payload, origin_url: window.location.origin });
      return r.data.order_id;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start PayPal checkout.");
      throw err;
    }
  };

  const onApprove = async (data, actions) => {
    try {
      const r = await axios.post(`${API}/payments/paypal/order/${data.orderID}/capture`);
      navigate(`/payment/success?session_id=${r.data.session_id}`);
    } catch (err) {
      if (err?.response?.status === 402) return actions.restart();
      toast.error(err?.response?.data?.detail || "PayPal could not complete the payment.");
    }
  };

  return (
    <div data-testid={testId} className={`relative z-0 ${disabled ? "opacity-50 pointer-events-none" : ""}`}>
      <PayPalButtons
        style={{ layout: "horizontal", color: "black", shape: "pill", height: 48, label: "paypal", tagline: false }}
        fundingSource="paypal"
        disabled={disabled}
        forceReRender={[disabled]}
        createOrder={createOrder}
        onApprove={onApprove}
        onError={(e) => { if (String(e?.message || "") !== "incomplete") toast.error("PayPal hit a snag — try again or pay by card."); }}
      />
    </div>
  );
};
